import React, { useEffect, useState } from "react";

import {
    Autocomplete,
    Chip,
    FormControl,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";

import useUpdateEffect from "hooks/useUpdateEffect";
import { useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";

import {
    postLeadAuthenticateLinkedin,
    postLeadLoadFormsLinkedin,
    postLeadRefreshLinkedin,
} from "helpers/Sources/linkedin";

import LinkedinLogin from "components/auth/linkedinAuth";

const LinkedinLeadSource = ({
    selectedLinkedinSource,
    setLinkedinData,
    shouldLoadLinkedinForms,
    setShouldLoadLinkedinForms,
    setIsLoggedIn,
    selectedConnectedSourceApiId,
    resetConnectedSourceSelection,
}) => {
    const language = useSelector((state) => state.languagesState.languages);
    const [isRefreshed, setIsRefreshed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingLoadForms, setIsLoadingLoadForms] = useState(false);

    const [name, setName] = useState("");
    const [sourceAccountID, setSourceAccountID] = useState(null);
    const [adAccounts, setAdAccounts] = useState([]);
    const [selectedAdAccountID, setSelectedAdAccountID] = useState(null);
    const [selectedAdAccountReference, setSelectedAdAccountReference] =
        useState(null);

    const [forms, setForms] = useState([]);
    const [selectedForms, setSelectedForms] = useState([]);
    useUpdateEffect(() => {
        if (!selectedConnectedSourceApiId) {
            setName("");
            setSourceAccountID(null);
            setAdAccounts([]);
            setSelectedAdAccountID(null);
            setSelectedAdAccountReference(null);
            setForms([]);
            setSelectedForms([]);
        }
    }, [selectedConnectedSourceApiId]);
    const onRefresh = async (payload) => {
        setIsLoading(true);
        const { code, state } = payload;
        const response = await postLeadRefreshLinkedin({
            code,
            state,
            source_account_id: sourceAccountID,
        });
        setAdAccounts(
            response?.data?.map((account) => ({
                reference: account?.ad_account_ref,
                name: account?.ad_account_name,
                id: account?.ad_account_id,
            })),
        );
        setSelectedForms([]);
        setIsLoading(false);
        setIsRefreshed(true);
        setShouldLoadLinkedinForms(true);
    };

    const onLogin = async (payload) => {
        setIsLoading(true);
        const { code, state } = payload;
        const response = await postLeadAuthenticateLinkedin({
            code,
            state,
            name,
        });
        setIsLoggedIn(true);
        if (response.status) {
            setAdAccounts(response.data?.ad_accounts);
            setSourceAccountID(response.data?.source_account_id);
        }
        setIsLoading(false);
    };

    const fetchForms = async () => {
        setIsLoadingLoadForms(true);
        const response = await postLeadLoadFormsLinkedin({
            ad_account_reference: selectedAdAccountReference,
            source_account_id: sourceAccountID,
        });
        if (response.status) {
            setForms(response?.data?.forms);
        }
        setIsLoadingLoadForms(false);
    };

    useEffect(() => {
        if (selectedAdAccountReference && shouldLoadLinkedinForms) {
            fetchForms();
        }
    }, [selectedAdAccountReference, shouldLoadLinkedinForms]);

    useUpdateEffect(() => {
        setLinkedinData({
            selectedAdAccountID: selectedAdAccountID,
            selectedForms,
            sourceAccountID,
        });
    }, [selectedAdAccountID, selectedForms]);

    const handleSelectedLinkedinSourceAdAccountChange = (reference) => {
        if (!selectedLinkedinSource) return;
        const pages = selectedLinkedinSource?.sourcePages;
        const adAccount = pages?.find(
            (account) => account?.reference === reference,
        );

        const allForms = adAccount?.page_forms?.map((form) => ({
            form_id: form?.id,
            name: form?.name,
        }));
        setSelectedForms([]);
        setForms(allForms || []);
    };

    useEffect(() => {
        if (selectedLinkedinSource) {
            setName(selectedLinkedinSource?.name);
            const pages = selectedLinkedinSource?.sourcePages;
            const selectedPage = pages?.find((page) => page?.is_selected);
            const pageForms = selectedPage?.page_forms;
            const adAccounts = pages?.map((page) => ({
                name: page?.page_name,
                id: page?.id,
                reference: page?.reference,
            }));
            const allForms = selectedPage?.page_forms?.map((form) => ({
                form_id: form?.id,
                name: form?.name,
            }));

            setSourceAccountID(selectedLinkedinSource?.id);
            setSelectedAdAccountReference(selectedPage?.reference);
            setSelectedAdAccountID(selectedPage?.id);
            setAdAccounts(adAccounts || []);
            setSelectedForms(
                pageForms
                    ?.filter((pageForm) => pageForm?.is_selected)
                    ?.map((form) => ({
                        form_id: form?.id,
                        name: form?.name,
                    })) || [],
            );
            setForms(allForms || []);
        }
    }, [selectedLinkedinSource]);

    return (
        <div>
            <FormControl
                className="mb-3 fb-source"
                style={{
                    width: "100%",
                    position: "relative",
                }}
            >
                {selectedLinkedinSource && (
                    <div className="linkedin-established-area">
                        <span className="source-established-label">
                            <img src="/assets/images/typeImages/lead/sources/linkedin.png" />
                            {"Linkedin connection established"}
                        </span>
                        <button
                            type="button"
                            className="source-established-reset"
                            onClick={resetConnectedSourceSelection}
                        >
                            {language?.lead_wizard?.reselect_source ??
                                "Reselect source"}
                        </button>
                    </div>
                )}
                <TextField
                    label={"Name"}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    disabled={selectedLinkedinSource}
                />
            </FormControl>
            {!adAccounts?.length && !selectedLinkedinSource && (
                <>
                    <LinkedinLogin
                        disabled={!name}
                        onLogin={onLogin}
                        isLoading={isLoading}
                    />
                </>
            )}

            {!!adAccounts?.length && (
                <div className="mb-3">
                    <FormControl
                        style={{
                            width: "100%",
                        }}
                    >
                        <Select
                            label={"Ad Account"}
                            value={selectedAdAccountReference}
                            onChange={(e) => {
                                setShouldLoadLinkedinForms(true);
                                setSelectedAdAccountReference(e.target.value);
                                setSelectedAdAccountID(
                                    adAccounts?.find(
                                        (account) =>
                                            account?.reference ===
                                            e.target.value,
                                    )?.id,
                                );
                                handleSelectedLinkedinSourceAdAccountChange(
                                    e.target.value,
                                );
                            }}
                        >
                            {adAccounts.map((item) => (
                                <MenuItem
                                    key={item?.reference}
                                    value={item?.reference}
                                >
                                    {item?.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <div className="position-relative mt-3">
                        {isLoadingLoadForms && (
                            <div
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    top: "40%",
                                    transform: "translateX(-50%)",
                                }}
                            >
                                <ClipLoader color={"#000"} size={17} />
                            </div>
                        )}
                        <Autocomplete
                            disabled={isLoadingLoadForms}
                            multiple
                            options={forms}
                            disableCloseOnSelect
                            value={selectedForms}
                            onChange={(event, newValue) =>
                                setSelectedForms(newValue)
                            }
                            getOptionLabel={(option) => option.name}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        label={option.name}
                                        {...getTagProps({ index })}
                                        key={option.id}
                                        onClick={() => {}}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Forms"
                                    className="text-field-ui-workaround"
                                />
                            )}
                        />
                    </div>
                </div>
            )}

            {selectedLinkedinSource && !isRefreshed && (
                <LinkedinLogin
                    label="Refresh Linkedin Forms"
                    disabled={false}
                    onLogin={onRefresh}
                    isLoading={isLoading}
                />
            )}
        </div>
    );
};

export default LinkedinLeadSource;
